import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  useDocExtractionEvalRunsLazyQuery,
  type EvalRunFieldsFragment,
} from "../../generated/graphql";
import { isTerminalStatus, runStatusColor } from "./eval-config";
import {
  avgScorePct,
  formatEffort,
  formatPct,
  modelIdOf,
  reasoningEffortOf,
} from "./runs-table-format";

const POLL_INTERVAL_MS = 5000;

const estimatedUsd = (run: EvalRunFieldsFragment): number | null => {
  const usd = (run.costEstimate as { usd?: number | null } | null)?.usd;
  return usd ?? null;
};

const formatUsd = (value: number | null): string =>
  value === null ? "—" : `$${value.toFixed(2)}`;

/**
 * Live table of eval runs. Polls while any run is non-terminal and stops once
 * every run has settled (COMPLETED / FAILED / CANCELLED), mirroring the
 * doc-scanning scan-job table. Rows link to the run detail route.
 */
const RunsTable = ({
  evalSetId,
  refreshKey,
}: {
  readonly evalSetId?: string | null;
  readonly refreshKey: number;
}) => {
  // Override the app-wide `errorPolicy: "ignore"` so a proxy/network failure
  // surfaces as an error (with retry) instead of masquerading as "No runs yet"
  // and silently halting polling.
  const [fetchRuns, { data, loading, error, startPolling, stopPolling }] =
    useDocExtractionEvalRunsLazyQuery({
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    });

  const runs = data?.docExtractionEvalRuns ?? [];
  const hasActiveRuns = runs.some((r) => !isTerminalStatus(r.status));

  const reload = useCallback(() => {
    void fetchRuns({
      variables: { evalSetId: evalSetId ?? undefined, limit: 100 },
    });
  }, [fetchRuns, evalSetId]);

  useEffect(() => {
    reload();
  }, [reload, refreshKey]);

  useEffect(() => {
    if (hasActiveRuns) {
      startPolling(POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [hasActiveRuns, startPolling, stopPolling]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography gutterBottom variant="h6">
        Runs
      </Typography>
      {error === undefined ? (
        loading && runs.length === 0 ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading runs…
            </Typography>
          </Stack>
        ) : runs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No runs yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Effort</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <Tooltip title="All-fields entity accuracy (available once scored)">
                  <TableCell align="right">Avg score</TableCell>
                </Tooltip>
                <TableCell>Mode</TableCell>
                <TableCell align="right">Est. cost</TableCell>
                <TableCell align="right">Actual cost</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => {
                const settled = run.completedCount + run.errorCount;
                const pct =
                  run.documentCount > 0
                    ? (settled / run.documentCount) * 100
                    : 0;
                return (
                  <TableRow key={run.id} hover>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/doc-extraction-evals/runs/${run.id}`}
                      >
                        {run.name}
                      </Link>
                    </TableCell>
                    <TableCell>{modelIdOf(run.config) ?? "—"}</TableCell>
                    <TableCell>
                      {formatEffort(reasoningEffortOf(run.config))}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={run.status}
                        color={runStatusColor(run.status)}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="caption">
                          {settled} / {run.documentCount}
                          {run.errorCount > 0 ? ` (${run.errorCount} err)` : ""}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(pct, 100)}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {formatPct(avgScorePct(run.aggregates))}
                    </TableCell>
                    <TableCell>{run.executionMode}</TableCell>
                    <TableCell align="right">
                      {formatUsd(estimatedUsd(run))}
                    </TableCell>
                    <TableCell align="right">
                      {formatUsd(run.actualCostUsd ?? null)}
                    </TableCell>
                    <TableCell>
                      {new Date(run.createdAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )
      ) : (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          Failed to load runs: {error.message}
        </Alert>
      )}
    </Paper>
  );
};

export default RunsTable;
